var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * A components subfield whose value is DERIVED from another field's subfield, and is
 * therefore not editable here (CZNA-718).
 *
 * <p>The case it exists for is `400 $d`. Life dates are a property of the person, but
 * `400` is repeatable, so until now the cataloguer retyped them into every variant of the
 * name — and NK named the consequence themselves: recording a death year means correcting
 * `$d` in `100` *and* in each `400`, and a miss leaves the record internally contradictory
 * with nothing to notice it. Measured on the live corpus (30. 8. 2026): 489 845 occurrences
 * of `400 $d` already match `100 $d` exactly — and 90 do not.
 *
 * <p>So the inputs are gone and the value is read off the source field live: change the year
 * of death in `100` and every variant follows on the same keystroke, before anything is
 * saved. The mirror into this subfield's own form state is what keeps the MARC preview and
 * the submitted payload honest — the server derives it again on write (`VariantLifeDatesStamper`),
 * which is what actually guarantees the invariant; this is the half the cataloguer can see.
 */
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { readComponentSubfield } from '../codec/codecRegistry';
import SubfieldWithComponents from './SubfieldWithComponents';
import ComponentRow from './ComponentRow';
import { subfieldReadOnlyInputStyle, subfieldInputColumnOffset } from './subfieldStyles';
import { componentInputId } from '../renderer/domIds';
import { resolveLabel } from '../renderer/labelResolver';
/**
 * The source occurrence is always index 0: `derivedFrom` names a heading field (`100`), which
 * is non-repeatable both in the packaged template and in the MARC catalog. A repeatable source
 * would have no single "the" value to derive from, so this stays deliberately un-generalized.
 */
var SOURCE_INSTANCE = 0;
/**
 * The subfield that carries the name proper (`$a`). The derived value is the dates OF this
 * occurrence's name, so an occurrence that has no name has nothing to date (CZNA-939).
 *
 * <p>NK reported it as "ve 400 visí ta data samotná": `400` is `minInstances: 1`, so every
 * personal record opens with one empty variant, and the mirror below wrote the person's life
 * dates into it on mount — visible in the form and in the MARC preview, and submitted. The
 * server refuses it now (`VariantLifeDatesStamper`), and this is the half the cataloguer sees;
 * if only the server half existed, the form would keep showing a value that the save deletes.
 */
export var NAME_SUBFIELD = 'a';
/**
 * Whether this field-instance carries a name, answered the way the SAVE answers it — because
 * the whole point is that the form and the stored record agree.
 *
 * <p>For a subfield with components that means the components, plus `importedValue`; it does
 * NOT mean `$a.value`. The blind review found why: `renderComponentSubfield` composes the
 * displayed `$a` on every render but never writes it back, and the load path seeds
 * `instance.a.value` from the stored row — so on an existing proposal that value stays behind
 * as the cataloguer empties the name. Reading it would leave the form showing life dates that
 * the save then deletes (`genericFormToProposalData` emits no `$a` row once every component is
 * blank), which is this ticket's own bug wearing the opposite sign.
 *
 * <p>`importedValue` is checked because the save re-emits it verbatim while the components are
 * unedited (CZNA-310): an Aleph heading whose flat `$a` would not parse into components has its
 * name only there, and it IS submitted. A subfield with no components at all is judged on
 * `value`, which is exactly what the save emits for it.
 *
 * <p>Exact parity with the save is NOT claimed, and does not need to be. The save's
 * `importedValue` path also re-decomposes under the LIVE `ind1`, so switching heading type on an
 * imported, unparsed `$a` can make it stop re-emitting while this still reads a name. What that
 * costs is a display that is briefly optimistic — never a wrong record, because
 * `VariantLifeDatesStamper` decides what is stored and asks the submitted rows, not this. The
 * alternative was to reimplement the save's decomposition here, which is a second copy of the
 * thing most likely to drift.
 */
export var hasNameValue = function (watch, pathPrefix, nameSf) {
    if (!nameSf)
        return false;
    var watchAny = watch;
    var read = function (suffix) { return (watchAny("".concat(pathPrefix, ".").concat(nameSf.code, ".").concat(suffix)) || '').trim(); };
    // The branch condition is `sf.components` truthiness, NOT its length — that is exactly what
    // `genericFormToProposalData` tests, and a subfield declaring `components: []` takes the
    // component path there (emitting nothing) while a length test would send it to `value`.
    if (nameSf.hasComponents && nameSf.components) {
        for (var _i = 0, _a = nameSf.components; _i < _a.length; _i++) {
            var comp = _a[_i];
            if (read("components.".concat(comp.componentType)))
                return true;
        }
        return !!read('importedValue');
    }
    return !!read('value');
};
var disabledInputStyle = __assign(__assign({}, subfieldReadOnlyInputStyle), { width: '100%', boxSizing: 'border-box' });
/** Under the inputs, not under the labels: it explains the values, not the names. */
var hintStyle = {
    fontSize: '12px',
    color: '#666',
    marginTop: '-4px',
    marginBottom: '8px',
    paddingLeft: subfieldInputColumnOffset,
};
var DerivedComponentSubfield = function (_a) {
    var sf = _a.sf, components = _a.components, pathPrefix = _a.pathPrefix, watch = _a.watch, nameFilled = _a.nameFilled;
    var intl = useIntl();
    var derivedFrom = sf.derivedFrom;
    // `pathPrefix` is `<root>.<tag>.<instance>`; the source lives under the same root.
    var root = pathPrefix.split('.')[0];
    var sourcePrefix = "".concat(root, ".").concat(derivedFrom.tag, ".").concat(SOURCE_INSTANCE, ".").concat(derivedFrom.subfield);
    var watchAny = watch;
    var sourceValues = {};
    for (var _i = 0, components_1 = components; _i < components_1.length; _i++) {
        var comp = components_1[_i];
        var live = watchAny("".concat(sourcePrefix, ".components.").concat(comp.componentType)) || '';
        // Shown blank rather than merely disabled: the display and the save must agree, and with
        // no name the save stores nothing here (CZNA-939).
        sourceValues[comp.componentType] = nameFilled ? live : '';
    }
    // CZNA-898 — THIS subfield's snapshot, not the source's: what the save writes
    // here is decided by this subfield's own stored row, and reading the source's
    // would show a value nothing ever persists. Once the mirror has synced, the
    // components match the snapshot and both sides answer with the stored value.
    var originals = {
        importedValue: watchAny("".concat(pathPrefix, ".").concat(sf.code, ".importedValue")),
        persisted: watchAny("".concat(pathPrefix, ".").concat(sf.code, ".persisted")),
    };
    var summarized = readComponentSubfield(sf, sourceValues, originals);
    return (React.createElement(SubfieldWithComponents, { code: sf.code, label: resolveLabel(sf, intl), value: summarized, required: sf.required, emptyPlaceholder: intl.formatMessage({
            id: nameFilled
                ? 'ui-czna-authorities.proposalForm.derivedFromEmpty'
                : 'ui-czna-authorities.proposalForm.derivedAwaitingName',
        }, { tag: derivedFrom.tag, subfield: derivedFrom.subfield }) },
        React.createElement("div", { "data-testid": "derived-subfield-".concat(derivedFrom.tag, "-").concat(derivedFrom.subfield) },
            components.map(function (comp) {
                var inputId = componentInputId(pathPrefix, sf.code, comp.componentType);
                return (React.createElement(ComponentRow, { key: comp.componentType, inputId: inputId, label: resolveLabel(comp, intl), readOnly: true },
                    React.createElement("input", { id: inputId, type: "text", value: sourceValues[comp.componentType], style: disabledInputStyle, disabled: true, readOnly: true })));
            }),
            React.createElement("div", { style: hintStyle }, intl.formatMessage({
                id: nameFilled
                    ? 'ui-czna-authorities.proposalForm.derivedFromHint'
                    : 'ui-czna-authorities.proposalForm.derivedAwaitingName',
            }, { tag: derivedFrom.tag, subfield: derivedFrom.subfield })))));
};
export default DerivedComponentSubfield;
export { SOURCE_INSTANCE };
// The `: null` return annotation is not decoration: the stripes build compiles with
// `noImplicitAny` on function expressions (TS7011), which the module's own
// `tsc -p tsconfig.json` does not — so a missing one passes locally and fails the bundle.
export var DerivedSubfieldSync = function (_a) {
    var sf = _a.sf, pathPrefix = _a.pathPrefix, applicable = _a.applicable, watch = _a.watch, setValue = _a.setValue;
    var derivedFrom = sf.derivedFrom;
    var root = pathPrefix.split('.')[0];
    var sourcePrefix = "".concat(root, ".").concat(derivedFrom.tag, ".").concat(SOURCE_INSTANCE, ".").concat(derivedFrom.subfield);
    var targetPrefix = "".concat(pathPrefix, ".").concat(sf.code);
    var components = sf.components || [];
    var watchAny = watch;
    var values = {};
    for (var _i = 0, components_2 = components; _i < components_2.length; _i++) {
        var comp = components_2[_i];
        var live = watchAny("".concat(sourcePrefix, ".components.").concat(comp.componentType)) || '';
        values[comp.componentType] = applicable ? live : '';
    }
    // Keyed on the RAW values, not on anything composed from them: two different splits can
    // compose to the same string, and keying on the composed one would skip the second write.
    // `targetPrefix` is in the key because `DynamicRepeatableField` keys occurrences on
    // `field.id`, so removing one shifts a survivor's path WITHOUT unmounting it.
    var mirrorKey = __spreadArray([
        targetPrefix,
        applicable ? 'on' : 'off'
    ], components.map(function (c) { return "".concat(c.componentType, "=").concat(values[c.componentType]); }), true).join('|');
    useEffect(function () {
        if (!setValue)
            return;
        for (var _i = 0, components_3 = components; _i < components_3.length; _i++) {
            var comp = components_3[_i];
            setValue("".concat(targetPrefix, ".components.").concat(comp.componentType), values[comp.componentType], { shouldDirty: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mirrorKey encodes the only inputs that may re-fire this
    }, [mirrorKey]);
    return null;
};
